import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Line } from 'react-chartjs-2';
import { fetchWithRetry } from '../utils/fetchWithRetry';
import { useSocket } from '../hooks/useSocket';
import { useCategories } from '../hooks/useCategories';
import { useBranch } from '../contexts/BranchContext';
import { getReportBranchHeaders } from '../utils/branchStorage';
import { getPaymentMethodLabel } from '../utils/paymentMethod';
import Toast from '../components/Toast';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

// Chart.js ตั้งสีตัวอักษร/เส้นตารางเริ่มต้นเป็นโทนเข้ม (#666) ซึ่งจมไปกับการ์ดธีมมืด
// ตั้งครั้งเดียวให้มีผลกับทุกกราฟในหน้า — หน้านี้เป็นหน้าเดียวในแอปที่ใช้ chart.js
// สีอ้างจาก --faint/--border ของ index.css ตรงๆ (canvas fillStyle ใช้ var() ไม่ได้ต้อง hardcode)
ChartJS.defaults.color = '#7d8496';
ChartJS.defaults.borderColor = '#262a35';

// Helper function to format numbers with comma separator
const formatNumber = (num) => {
  return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatHM = (dateStr) => {
  const d = new Date(dateStr);
  return d.toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit', hour12: false });
};

// สถานะจริงทั้งหมดที่ระบบรองรับ (ยืนยันจาก VALID_STATUS_TRANSITIONS ตอนแก้บั๊ก B) —
// สถานะอื่นที่ไม่รู้จัก (ไม่ควรเกิดจริง) fallback ไปโชว์ข้อความดิบสี --faint
const STATUS_LABELS = {
  submitted: { label: 'ส่งครัวแล้ว', color: 'var(--info)' },
  preparing: { label: 'กำลังทำ', color: 'var(--warn)' },
  served: { label: 'เสิร์ฟแล้ว', color: 'var(--ok)' },
  cancelled: { label: 'ยกเลิก', color: 'var(--danger)' },
};

const EXCLUDED_CATEGORY_NAME = 'เรียกพนักงาน';

// เครื่องพิมพ์ถือว่า "ออฟไลน์" เมื่อสถานะข้อความตรงกับกลุ่ม error เดิม (ไม่พบ/ล้มเหลว/ผิดพลาด)
// เกณฑ์เดียวกับที่ getPrinterStatusColor ใช้แยกสีแดงมาก่อนหน้านี้
const isPrinterOffline = (status) => {
  if (!status) return false;
  const s = status.toLowerCase();
  return s.includes('ไม่พบ') || s.includes('ล้มเหลว') || s.includes('ผิดพลาด') || s.includes('error');
};

export default function Dashboard({ apiUrl, printerUrl }) {
  const { currentBranch, effectiveBranchId, isAllBranches, loading: branchLoading, isOwner } = useBranch();
  const navigate = useNavigate();
  // สรุปเวอร์ชันโค้ดของทุกสาขา (เฉพาะเจ้าของร้าน) — ยิงครั้งเดียวตอนเข้าหน้า ไม่ poll ตามที่สั่ง
  // รายละเอียดทีละสาขาอยู่ที่หน้า "จัดการสาขา" (BranchManagement.jsx) อยู่แล้ว ที่นี่โชว์แค่
  // ป้ายเตือนรวมสั้นๆ ให้เจ้าของร้านรู้ว่าต้องไปดูเพิ่ม ไม่ต้องคำนวณ/แสดงราย field ซ้ำที่นี่
  const [branchVersionAlert, setBranchVersionAlert] = useState(null); // { needsUpdate } | null
  const [stats, setStats] = useState(null);
  const [salesTrend, setSalesTrend] = useState([]);
  const { categories } = useCategories(apiUrl, effectiveBranchId); // Use cached categories (this branch's own)
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('today');
  // "N วัน" ของกราฟเทรนด์รวมเข้ากับตัวเลือกช่วงเวลาบนสุดแล้ว (อนุมัติแล้ว): วันนี้/7 วัน → 7,
  // 30 วัน → 30 — ไม่มี dropdown แยกให้เลือกเองอีกต่อไป
  const isPastDay = dateRange === 'yesterday';
  const trendDays =dateRange === 'month' ? 30 : 7;
  const [trendComparison, setTrendComparison] = useState(null);
  const [showComparison, setShowComparison] = useState(true);
  const [printers, setPrinters] = useState([]); // [{id, name}] — from GET /printer/status
  const [printerStatus, setPrinterStatus] = useState({}); // { [printerId]: statusText }
  const [printerLoadState, setPrinterLoadState] = useState('loading'); // 'loading' | 'ok' | 'error'
  // ตัวพิมพ์ของสาขาหยิบงานจากคิวอยู่จริงไหม (แยกจากสถานะ "เครื่องพิมพ์เสียบอยู่") — GET /printer/daemon-status
  const [daemonHealth, setDaemonHealth] = useState(null);
  const [printerLoadError, setPrinterLoadError] = useState(null);
  const [allTables, setAllTables] = useState([]); // รายการโต๊ะทั้งหมด (ไม่กรองสถานะ) — ใช้นับจำนวนโต๊ะทั้งหมด + เลขโต๊ะที่เปิดอยู่
  const [topItemsExpanded, setTopItemsExpanded] = useState(false);
  const [message, setMessage] = useState(null); // { type: 'success'|'error', text } — แทน alert() เดิม
  const [userStats, setUserStats] = useState({
    activeUsers: 0,
    todayVisitors: 0,
    totalVisitors: 0
  });
  const [revenueStats, setRevenueStats] = useState({
    paid: 0,        // ชำระแล้ว (จาก transactions)
    unpaid: 0,      // ยังไม่ชำระ (จาก orders + tables)
    total: 0,       // รวมทั้งหมด
    byPaymentMethod: {} // แยกยอดที่ชำระแล้วตามวิธีชำระเงิน
  });

  // WebSocket: Listen for real-time updates
  useSocket(apiUrl, {
    'stats:updated': (data) => {
      console.log('[Dashboard] Stats updated:', data);
      setUserStats(prev => ({ ...prev, ...data.userStats }));
    },
    'revenue:updated': (data) => {
      console.log('[Dashboard] Revenue updated:', data);
      setRevenueStats(prev => ({ ...prev, ...data.revenueStats }));
    },
    'printer:status': (data) => {
      console.log('[Dashboard] Printer status:', data);
      setPrinterStatus(prev => ({ ...prev, ...data }));
    },
    'order:created': () => {
      // Reload revenue when new order created
      loadRevenueStats();
    },
    'transaction:created': () => {
      // Reload revenue when transaction created (checkout)
      loadRevenueStats();
    }
  }, {
    token: localStorage.getItem('adminToken'),
    // ไม่ใส่ branchId ตอนดู "รวมทุกสาขา" — เข้าโหมด HQ rollup แทนที่จะบังคับสาขาแรก
    branchId: isAllBranches ? null : effectiveBranchId
  });

  useEffect(() => {
    loadDashboard();
    // loadCategories(); // REMOVED: Now using useCategories hook with cache
    loadUserStats();
    loadRevenueStats();

    // No more polling interval - using WebSocket events instead
  }, [dateRange]);

  // แยก effect ต่างหาก รอ currentBranch โหลดเสร็จก่อนค่อยเช็คสถานะเครื่องพิมพ์ — เดิมเช็คแค่
  // effectiveBranchId (ได้ค่าจาก localStorage ทันทีตั้งแต่ก่อน branches ยิง fetch เสร็จด้วยซ้ำ)
  // ทำให้ currentBranch ยังเป็น null อยู่ตอนเรียก แล้ว fallback ไปใช้ printerUrl ตัว global
  // ผิดสาขาไปเรื่อย ๆ จนกว่าจะมีคนคลิกการ์ดเพื่อลองใหม่เอง — ต้องรอ !branchLoading ด้วย
  // เช็คสถานะเครื่องพิมพ์ต้องรู้ว่าเป็นสาขาไหนแน่ๆ — ตอนเลือก "รวมทุกสาขา" ข้ามไปเลย
  // ไม่งั้น effectiveBranchId/currentBranch จะ fallback ไปสาขาแรกตามลำดับตัวอักษรแทน
  useEffect(() => {
    if (!branchLoading && effectiveBranchId && !isAllBranches) checkPrinterStatus();
  }, [branchLoading, effectiveBranchId, currentBranch, isAllBranches]);

  // เช็คเวอร์ชันโค้ดของทุกสาขาครั้งเดียว — เฉพาะเจ้าของร้าน (ตรงกับ requireOwnerAdmin ฝั่ง
  // backend) ไม่ poll เพราะ backend ต้องยิง Cloudflare Tunnel ไปทุกสาขาจริงทุกครั้งที่เรียก
  useEffect(() => {
    if (!isOwner) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${apiUrl}/printer/branch-versions`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` }
        });
        const body = await res.json().catch(() => null);
        if (cancelled || !res.ok || !body?.data) return;
        const needsUpdate = body.data.branches.filter(b =>
          b.reachable === false || (b.outdated && (b.behindDays === null || b.behindDays > 7))
        ).length;
        if (needsUpdate > 0) setBranchVersionAlert({ needsUpdate });
      } catch {
        // เงียบไว้ — ป้ายเตือนแค่ไม่ขึ้น ไม่กระทบแดชบอร์ดหลัก
      }
    })();
    return () => { cancelled = true; };
  }, [apiUrl, isOwner]);

  // Poll printer status periodically so the card reflects USB plug/unplug
  // without the user having to refresh the page.
  useEffect(() => {
    if (branchLoading || !effectiveBranchId || isAllBranches) return;
    const interval = setInterval(checkPrinterStatus, 15000);
    return () => clearInterval(interval);
  }, [branchLoading, effectiveBranchId, currentBranch, isAllBranches]);

  // โหลดรายการโต๊ะทั้งหมด (ไม่กรองสถานะ) ไว้ใช้แค่นับจำนวนรวม + เลขโต๊ะที่เปิดอยู่ในการ์ด
  // "โต๊ะที่ใช้งาน" — GET /api/tables มีอยู่แล้ว (TableManagement.jsx ใช้อยู่) ไม่ต้องเพิ่ม endpoint ใหม่
  // ข้าม "รวมทุกสาขา" เพราะจำนวนโต๊ะเป็นแนวคิดต่อสาขา ไม่มีความหมายเป็น rollup
  useEffect(() => {
    if (branchLoading || isAllBranches) { setAllTables([]); return; }
    loadTablesList();
  }, [branchLoading, effectiveBranchId, isAllBranches]);

  useEffect(() => {
    loadSalesTrend();
  }, [dateRange, showComparison]);

  // ใช้ร่วมกันระหว่าง loadDashboard และ loadRevenueStats ให้ทั้งสอง endpoint กรองตาม
  // ช่วงวันที่เดียวกันเสมอ — เดิม loadRevenueStats ไม่ส่ง query นี้เลย การ์ด "รายได้"
  // เลยค้างเป็นของวันนี้ตลอด ไม่ว่าจะกดปุ่ม 7 วัน/30 วัน
  const buildDateRangeParams = () => {
    const params = new URLSearchParams();
    const now = new Date();

    if (dateRange === 'today') {
      const startOfDay = new Date(now);
      startOfDay.setHours(0, 0, 0, 0);
      const endOfDay = new Date(now);
      endOfDay.setHours(23, 59, 59, 999);
      params.append('startDate', startOfDay.toISOString());
      params.append('endDate', endOfDay.toISOString());
    } else if (dateRange === 'yesterday') {
      const startOfYesterday = new Date(now);
      startOfYesterday.setDate(startOfYesterday.getDate() - 1);
      startOfYesterday.setHours(0, 0, 0, 0);
      const endOfYesterday = new Date(startOfYesterday);
      endOfYesterday.setHours(23, 59, 59, 999);
      params.append('startDate', startOfYesterday.toISOString());
      params.append('endDate', endOfYesterday.toISOString());
    } else if (dateRange === 'week') {
      const startOfWeek = new Date(now.setDate(now.getDate() - 7));
      params.append('startDate', startOfWeek.toISOString());
    } else if (dateRange === 'month') {
      const startOfMonth = new Date(now.setDate(now.getDate() - 30));
      params.append('startDate', startOfMonth.toISOString());
    }

    return params;
  };

  const loadDashboard = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const params = buildDateRangeParams();

      const response = await fetchWithRetry(`${apiUrl}/dashboard/stats?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          ...getReportBranchHeaders()
        }
      });

      if (response.ok) {
        const result = await response.json();
        setStats(result.data);
      }
    } catch (err) {
      console.error('Failed to load dashboard:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadSalesTrend = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetchWithRetry(`${apiUrl}/dashboard/sales-trend?days=${trendDays}&compare=${showComparison}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          ...getReportBranchHeaders()
        }
      });

      if (response.ok) {
        const result = await response.json();
        setSalesTrend(result.data);
        setTrendComparison(result.comparison);
      }
    } catch (err) {
      console.error('Failed to load sales trend:', err);
    }
  };

  // REMOVED: loadCategories function - now using useCategories hook with localStorage cache
  // This reduces API calls by ~90% with 5-minute cache + WebSocket invalidation

  const getCategoryName = (categoryId) => {
    const category = categories.find(c => (c.id || c._id) === categoryId);
    return category ? category.name : categoryId.slice(-6);
  };

  const loadUserStats = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetchWithRetry(`${apiUrl}/api/analytics/users`, {
        headers: { Authorization: `Bearer ${token}`, ...getReportBranchHeaders() }
      });
      const data = await response.json();
      setUserStats({
        activeUsers: data.activeUsers || 0,
        todayVisitors: data.todayVisitors || 0,
        totalVisitors: data.totalVisitors || 0
      });
    } catch (err) {
      console.error('Failed to load user stats:', err);
    }
  };

  const loadRevenueStats = async () => {
    try {
      const token = localStorage.getItem('adminToken');

      // ใช้ endpoint ใหม่ที่คำนวณ revenue ทั้งหมดในครั้งเดียว (แก้ N+1 query)
      const params = buildDateRangeParams();
      const response = await fetchWithRetry(`${apiUrl}/api/transactions/revenue?${params}`, {
        headers: { 'Authorization': `Bearer ${token}`, ...getReportBranchHeaders() }
      });
      const data = await response.json();

      if (data.data) {
        setRevenueStats({
          paid: data.data.paid || 0,
          unpaid: data.data.unpaid || 0,
          total: data.data.total || 0,
          byPaymentMethod: data.data.byPaymentMethod || {}
        });
      }
    } catch (err) {
      console.error('Failed to load revenue stats:', err);
    }
  };

  const loadTablesList = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetchWithRetry(`${apiUrl}/api/tables`, {
        headers: { 'Authorization': `Bearer ${token}`, ...getReportBranchHeaders() }
      });
      const result = await response.json();
      const data = result.data || result;
      setAllTables(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load tables list:', err);
    }
  };

  const checkPrinterStatus = async () => {
    if (isAllBranches) return;

    // เช็กตัวพิมพ์แยกจากเช็กเครื่องพิมพ์ — อ่านจากฐานข้อมูลของ cloud ตรง ๆ ไม่ผ่านเครื่องสาขา (ซึ่งอาจเป็นตัวที่พัง)
    // พลาดตรงนี้ก็แค่ไม่โชว์เตือน ไม่ให้กระทบแถบเครื่องพิมพ์ด้านล่าง
    try {
      const daemonRes = await fetch(`${apiUrl}/printer/daemon-status`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`,
          'X-Branch-Id': effectiveBranchId
        }
      });
      if (daemonRes.ok) {
        const body = await daemonRes.json();
        setDaemonHealth(body.data || null);
      }
    } catch { /* ignore */ }

    try {
      const token = localStorage.getItem('adminToken');
      // ยิงผ่าน apiUrl (cloud) เสมอ ไม่คุยกับ tunnel ของสาขาตรงๆ จากเบราว์เซอร์เลย —
      // cloud เป็นคนตัดสินใจเองว่าต้อง proxy ต่อไปเครื่อง daemon จริงของสาขาไหม (ดู
      // printer.route.js) วิธีนี้ตัดปัญหา CORS + Cloudflare Access login ที่เกิดตอนยิง
      // ตรงไปที่ tunnel จากเบราว์เซอร์ออกไปทั้งก้อน
      const response = await fetchWithRetry(`${apiUrl}/printer/status`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Branch-Id': effectiveBranchId
        }
      });

      if (response.ok) {
        const result = await response.json();
        const data = result.data || {};
        setPrinters(Object.entries(data).map(([id, s]) => ({ id, name: s.name || id })));
        setPrinterStatus(
          Object.fromEntries(Object.entries(data).map(([id, s]) => [id, s.status || 'ไม่ทราบสถานะ']))
        );
        setPrinterLoadState('ok');
        setPrinterLoadError(null);
      } else {
        setPrinterLoadState('error');
        setPrinterLoadError(`เชื่อมต่อเครื่องพิมพ์ของสาขาไม่สำเร็จ (HTTP ${response.status})`);
      }
    } catch (err) {
      console.error('Failed to check printer status:', err);
      setPrinterLoadState('error');
      setPrinterLoadError(err.message || 'เชื่อมต่อไม่สำเร็จ');
    }
  };

  // POST /printer/test แค่ "เข้าคิว" งานพิมพ์ใน MongoDB แล้ว return ทันที — การพิมพ์จริง
  // เกิดทีหลังตอน Local Daemon หยิบงานไปทำ ต้อง poll สถานะจริงของ job แทนที่จะเชื่อ
  // response.ok เฉยๆ ไม่งั้นต่อให้ daemon ล่มหรือเครื่องพิมพ์หมดกระดาษ ก็จะขึ้น "สำเร็จ" อยู่ดี
  const pollPrintJobStatus = async (jobId, timeoutMs = 15000, intervalMs = 1000) => {
    const token = localStorage.getItem('adminToken');
    const deadline = Date.now() + timeoutMs;

    while (Date.now() < deadline) {
      await new Promise(resolve => setTimeout(resolve, intervalMs));
      try {
        // /printer/test/:jobId แค่อ่านสถานะจาก MongoDB เอง ไม่ต้องแตะ LAN ของสาขา
        // เลย เรียกผ่าน apiUrl (cloud) ได้ตรงๆ ไม่ต้องผ่าน tunnel
        const res = await fetch(`${apiUrl}/printer/test/${jobId}`, {
          headers: { 'Authorization': `Bearer ${token}`, 'X-Branch-Id': effectiveBranchId }
        });
        if (res.ok) {
          const { data } = await res.json();
          if (data.status === 'completed' || data.status === 'failed') return data;
        }
      } catch (_) {
        // เครือข่ายสะดุดชั่วคราว — poll รอบถัดไปต่อ ไม่ยกเลิกทันที
      }
    }
    return { status: 'timeout' };
  };

  const testPrinter = async (printerId) => {
    if (isAllBranches) {
      setMessage({ type: 'error', text: 'กรุณาเลือกสาขาที่แน่นอนก่อนทดสอบเครื่องพิมพ์ — เลือก "รวมทุกสาขา" ไม่ได้' });
      return;
    }
    const printerName = printers.find(p => p.id === printerId)?.name || printerId;
    setPrinterStatus(prev => ({
      ...prev,
      [printerId]: 'กำลังทดสอบ...'
    }));

    try {
      const token = localStorage.getItem('adminToken');
      // /printer/test แค่เข้าคิวงานใน MongoDB เอง ไม่ต้องแตะ LAN ของสาขา เรียกผ่าน
      // apiUrl (cloud) ได้ตรงๆ ไม่ต้องผ่าน tunnel ของสาขา
      const response = await fetchWithRetry(`${apiUrl}/printer/test`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-Branch-Id': effectiveBranchId
        },
        body: JSON.stringify({ printerId })
      });

      const result = await response.json();

      if (!response.ok) {
        setPrinterStatus(prev => ({
          ...prev,
          [printerId]: '❌ ทดสอบล้มเหลว'
        }));
        setMessage({ type: 'error', text: `ทดสอบเครื่องพิมพ์${printerName}ล้มเหลว: ${result.error?.message || 'Unknown error'}` });
      } else if (!result.data?.jobId) {
        // โหมด local-direct — พิมพ์เสร็จแบบ synchronous แล้วตอน response กลับมาถึงจะเชื่อได้จริง
        setPrinterStatus(prev => ({
          ...prev,
          [printerId]: '✅ ทดสอบสำเร็จ'
        }));
        setMessage({ type: 'success', text: `ทดสอบเครื่องพิมพ์${printerName}สำเร็จ` });
      } else {
        // โหมด cloud — job เพิ่งเข้าคิว ต้องรอ Local Daemon พิมพ์จริงก่อนค่อยฟันธงผล
        setPrinterStatus(prev => ({
          ...prev,
          [printerId]: '⏳ รอ Local Daemon พิมพ์จริง...'
        }));

        const finalStatus = await pollPrintJobStatus(result.data.jobId);

        if (finalStatus.status === 'completed') {
          setPrinterStatus(prev => ({
            ...prev,
            [printerId]: '✅ ทดสอบสำเร็จ'
          }));
          setMessage({ type: 'success', text: `ทดสอบเครื่องพิมพ์${printerName}สำเร็จ` });
        } else if (finalStatus.status === 'failed') {
          setPrinterStatus(prev => ({
            ...prev,
            [printerId]: '❌ ทดสอบล้มเหลว'
          }));
          setMessage({ type: 'error', text: `ทดสอบเครื่องพิมพ์${printerName}ล้มเหลว: ${finalStatus.error || 'เครื่องพิมพ์ไม่ตอบสนอง'}` });
        } else {
          setPrinterStatus(prev => ({
            ...prev,
            [printerId]: '⚠️ ไม่ทราบผล (รอนานเกินไป)'
          }));
          setMessage({ type: 'error', text: `ส่งคำสั่งทดสอบเครื่องพิมพ์${printerName}แล้ว แต่รอผลจาก Local Daemon นานเกินไป — ตรวจสอบว่า daemon ทำงานอยู่หรือไม่` });
        }
      }
    } catch (err) {
      console.error('Printer test error:', err);
      setPrinterStatus(prev => ({
        ...prev,
        [printerId]: '❌ เกิดข้อผิดพลาด'
      }));
      setMessage({ type: 'error', text: `เกิดข้อผิดพลาดในการทดสอบเครื่องพิมพ์${printerName}` });
    }

    // Reload actual printer status after 3 seconds
    setTimeout(() => {
      checkPrinterStatus();
    }, 3000);
  };

  // Sales trend chart data with comparison (memoized)
  // Must be before early returns to follow Rules of Hooks
  const trendChartData = useMemo(() => ({
    labels: salesTrend.map(day => new Date(day.date).toLocaleDateString('th-TH', { day: 'numeric', month: 'short' })),
    datasets: [
      {
        label: `ช่วงนี้ (${trendDays} วัน)`,
        data: salesTrend.map(day => day.revenue),
        borderColor: '#6ea8fe',
        backgroundColor: 'rgba(110, 168, 254, 0.12)',
        borderWidth: 3,
        pointRadius: 3,
        pointBackgroundColor: '#6ea8fe',
        fill: true,
        tension: 0.4
      },
      ...(showComparison && trendComparison?.previous ? [{
        label: `ช่วงก่อนหน้า`,
        data: trendComparison.previous.map(day => day.revenue),
        borderColor: '#7d8496',
        backgroundColor: 'rgba(125, 132, 150, 0.05)',
        borderDash: [5, 5],
        pointRadius: 0,
        fill: true,
        tension: 0.4
      }] : [])
    ]
  }), [salesTrend, trendDays, showComparison, trendComparison]);

  // เมนูยอดนิยม Top 5 (การ์ดแรก) / Top 10 (กด "ดูทั้งหมด") — แถบสีเดียวธรรมดา ไม่ใช้ chart.js
  // เพราะสเปกต้องการคุมสีทีละอันดับ (อันดับ 1 ทึบ ที่เหลือจาง) ง่ายกว่าด้วย CSS ตรงๆ
  const topItemsList = useMemo(() => {
    const items = (stats?.topSellingItems || []).slice(0, topItemsExpanded ? 10 : 5);
    const max = items.length > 0 ? items[0].quantity : 0;
    return items.map((item, index) => ({
      ...item,
      pct: max > 0 ? Math.round((item.quantity / max) * 100) : 0,
      opacity: index === 0 ? 1 : 0.8
    }));
  }, [stats?.topSellingItems, topItemsExpanded]);

  // สั่งตามหมวด — เดิมเป็นโดนัท เปลี่ยนเป็นแถบแนวนอนเรียงมากไปน้อย % ของจำนวนที่สั่ง
  // ไม่นับหมวด "เรียกพนักงาน" (ชื่อเดียวกับที่ตัดใน /by-category ฝั่งรายงานยอดขาย)
  const categoryBars = useMemo(() => {
    const raw = (stats?.categoryOrders || [])
      .map(cat => ({ name: getCategoryName(cat.category), count: cat.count }))
      .filter(c => c.name !== EXCLUDED_CATEGORY_NAME);
    const total = raw.reduce((sum, c) => sum + c.count, 0);
    return raw
      .map(c => ({ ...c, pct: total > 0 ? Math.round((c.count / total) * 1000) / 10 : 0 }))
      .sort((a, b) => b.count - a.count);
  }, [stats?.categoryOrders, categories]);

  // ที่จ่ายแล้ว แยกตามช่องทาง — เรียงมากไปน้อย ความยาวแถบเทียบกับยอดจ่ายแล้วทั้งหมด (revenueStats.paid)
  const paymentMethodBars = useMemo(() => {
    const entries = Object.entries(revenueStats.byPaymentMethod || {});
    return entries
      .map(([method, { total }]) => ({
        method,
        label: getPaymentMethodLabel(method),
        total,
        pct: revenueStats.paid > 0 ? Math.min(100, Math.round((total / revenueStats.paid) * 100)) : 0
      }))
      .sort((a, b) => b.total - a.total);
  }, [revenueStats.byPaymentMethod, revenueStats.paid]);

  const openTables = useMemo(
    () => allTables.filter(t => t.status === 'occupied').map(t => t.code).filter(Boolean),
    [allTables]
  );

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <div style={{ fontSize: '1.5rem' }}>📊</div>
        <p>กำลังโหลดข้อมูล...</p>
      </div>
    );
  }

  if (!stats) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p>ไม่สามารถโหลดข้อมูลได้</p>
      </div>
    );
  }

  const offlinePrinters = printers.filter(p => isPrinterOffline(printerStatus[p.id]));
  const onlineCount = printers.length - offlinePrinters.length;
  const daemonProblem = daemonHealth?.problem === true;
  const fmtWait = (sec) => (sec == null ? '' : sec >= 120 ? `${Math.round(sec / 60)} นาที` : `${sec} วินาที`);
  const rangeLabel = dateRange === 'today' ? 'วันนี้' : dateRange === 'yesterday' ? 'เมื่อวานนี้' : dateRange === 'week' ? '7 วันล่าสุด' : '30 วันล่าสุด';

  return (
    <div style={{ padding: 'clamp(0.75rem, 4vw, 2rem)', maxWidth: '1400px', width: '100%', margin: '0 auto' }}>
      <Toast message={message} onDismiss={() => setMessage(null)} />

      {/* Header + ตัวเลือกช่วงเวลา (คุมทั้ง KPI และกราฟเทรนด์ร่วมกัน — รวมมาจาก dropdown
          "N วัน" เดิมของกราฟที่ตัดออกไปแล้ว) */}
      <div style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          {[
            { key: 'today', label: 'วันนี้' },
            { key: 'yesterday', label: 'เมื่อวานนี้' },
            { key: 'week', label: '7 วัน' },
            { key: 'month', label: '30 วัน' }
          ].map(opt => (
            <button
              key={opt.key}
              onClick={() => setDateRange(opt.key)}
              style={{
                padding: '0.5rem 1rem',
                minHeight: '44px',
                borderRadius: '8px',
                border: dateRange === opt.key ? 'none' : '1px solid var(--border-strong)',
                background: dateRange === opt.key ? 'var(--accent)' : 'var(--surface-2)',
                color: dateRange === opt.key ? '#1a1206' : 'var(--text-2)',
                cursor: 'pointer',
                fontWeight: 600
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* แถบสถานะเครื่องพิมพ์ — บางแถบเดียวแทนการ์ด 3 ใบเดิม */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: '1rem',
        padding: '0.9rem 1.25rem',
        borderRadius: '12px',
        marginBottom: '1.5rem',
        background: (offlinePrinters.length > 0 || daemonProblem) ? '#2a1517' : 'var(--surface)',
        border: `1px solid ${(offlinePrinters.length > 0 || daemonProblem) ? '#8a2f33' : 'var(--border)'}`
      }}>
        <span style={{ color: 'var(--text-2)', fontWeight: 600, flexShrink: 0 }}>🖨️ เครื่องพิมพ์</span>

        {printers.length === 0 ? (
          <span style={{ color: 'var(--muted)', fontSize: '0.9rem' }}>
            {printerLoadState === 'loading' ? '⏳ กำลังโหลด...' : printerLoadState === 'error' ? `❌ ${printerLoadError}` : 'ไม่มีเครื่องพิมพ์ที่ตั้งค่าไว้'}
          </span>
        ) : (
          <div className="printer-strip-list" style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem 1rem' }}>
            {printers.map(printer => {
              const offline = isPrinterOffline(printerStatus[printer.id]);
              return (
                <span
                  key={printer.id}
                  onClick={() => testPrinter(printer.id)}
                  title="คลิกเพื่อทดสอบพิมพ์"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer', whiteSpace: 'nowrap', color: offline ? 'var(--text)' : 'var(--text-2)', fontWeight: offline ? 700 : 500 }}
                >
                  <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: offline ? 'var(--danger)' : 'var(--ok)', flexShrink: 0 }} />
                  {printer.name}{offline ? ' — ออฟไลน์' : ''}
                </span>
              );
            })}
          </div>
        )}

        {daemonProblem && (
          <span style={{ flexBasis: '100%', color: 'var(--danger)', fontWeight: 700, fontSize: '0.95rem' }}>
            ⚠ ระบบพิมพ์ของสาขาไม่ทำงาน — ใบสั่งอาหารจะไม่ออกจนกว่าจะแก้
            {daemonHealth.pending > 0 && ` (ค้างในคิว ${daemonHealth.pending} ใบ รอนานสุด ${fmtWait(daemonHealth.oldestPendingSeconds)})`}
            {daemonHealth.alive === false && daemonHealth.lastSeenSeconds != null && ` · ตัวพิมพ์เงียบมา ${fmtWait(daemonHealth.lastSeenSeconds)}`}
            {' '}— ให้รีสตาร์ต "Shabu QR - Local API" ที่เครื่องของสาขา
          </span>
        )}

        {branchVersionAlert && (
          <span
            onClick={() => navigate('/branches')}
            title="ไปหน้าจัดการสาขาเพื่อดูรายละเอียด"
            style={{
              marginLeft: 'auto',
              color: 'var(--warn)',
              fontSize: '0.85rem',
              fontWeight: 700,
              cursor: 'pointer',
              whiteSpace: 'nowrap'
            }}
          >
            ⚠ {branchVersionAlert.needsUpdate} สาขาต้องอัปเดต
          </span>
        )}

        {printers.length > 0 && (
          <span className="printer-strip-online" style={{ color: 'var(--muted)', fontSize: '0.85rem', marginLeft: branchVersionAlert ? 0 : 'auto', flexShrink: 0 }}>
            กำลังออนไลน์ {onlineCount}
          </span>
        )}
      </div>

      {offlinePrinters.length > 0 && (
        <div style={{ marginTop: '-1rem', marginBottom: '1.5rem', fontSize: '0.85rem', color: '#ff9b9b' }}>
          ⚠️ {offlinePrinters.map(p => p.name).join(', ')} ออฟไลน์ — ออเดอร์/ใบเสร็จที่พิมพ์ผ่านเครื่องนี้จะไม่ถูกพิมพ์
        </div>
      )}

      {/* KPI grid: 4 คอลัมน์ x 2 แถว — การ์ด "รายได้" กิน 2x2 */}
      <div className="dashboard-kpi-grid" style={{ marginBottom: '1.5rem' }}>
        {/* รายได้ */}
        <div className="card dashboard-kpi-revenue" style={{ display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
            <div style={{ color: 'var(--muted)', fontSize: '0.9rem' }}>💰 รายได้{rangeLabel}</div>
            <div style={{ color: 'var(--muted)', fontSize: '0.85rem' }}>{stats.summary.paidBills} บิล</div>
          </div>
          <div
            onClick={() => navigate('/transaction-history?filter=today')}
            style={{ fontSize: '52px', fontWeight: 700, color: 'var(--text)', cursor: 'pointer', lineHeight: 1.1, marginBottom: '1rem' }}
          >
            {formatNumber(isPastDay ? revenueStats.paid : revenueStats.total)} ฿
          </div>

          {/* แถบสัดส่วน จ่ายแล้ว / รอชำระ */}
          <div style={{ marginBottom: '0.5rem' }}>
            <div style={{ display: 'flex', height: '10px', borderRadius: '999px', overflow: 'hidden', background: 'var(--surface-2)' }}>
              <div style={{ width: `${isPastDay ? (revenueStats.paid > 0 ? 100 : 0) : revenueStats.total > 0 ? (revenueStats.paid / revenueStats.total) * 100 : 0}%`, background: 'var(--ok)' }} />
              {!isPastDay && <div style={{ width: `${revenueStats.total > 0 ? (revenueStats.unpaid / revenueStats.total) * 100 : 0}%`, background: 'var(--warn)' }} />}
            </div>
            <div style={{ display: 'flex', gap: '1.25rem', marginTop: '0.5rem', fontSize: '0.8rem', flexWrap: 'wrap' }}>
              <span style={{ color: 'var(--text-2)' }}><span style={{ color: 'var(--ok)' }}>●</span> จ่ายแล้ว {formatNumber(revenueStats.paid)} ฿</span>
              {!isPastDay && <span style={{ color: 'var(--text-2)' }}><span style={{ color: 'var(--warn)' }}>●</span> รอชำระ {formatNumber(revenueStats.unpaid)} ฿</span>}
            </div>
          </div>

          {paymentMethodBars.length > 0 && (
            <div style={{ marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid var(--border)' }}>
              <div style={{ color: 'var(--faint)', fontSize: '0.8rem', marginBottom: '0.5rem' }}>ที่จ่ายแล้ว แยกตามช่องทาง</div>
              {paymentMethodBars.map(pm => (
                <div key={pm.method} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.4rem' }}>
                  <span style={{ color: 'var(--text-2)', fontSize: '0.8rem', width: '90px', flexShrink: 0, overflowWrap: 'break-word' }}>{pm.label}</span>
                  <div style={{ flex: 1, height: '6px', borderRadius: '999px', background: 'var(--surface-2)', minWidth: 0 }}>
                    <div style={{ width: `${pm.pct}%`, height: '100%', borderRadius: '999px', background: 'var(--info)' }} />
                  </div>
                  <span style={{ color: 'var(--text-2)', fontSize: '0.8rem', flexShrink: 0 }}>{formatNumber(pm.total)} ฿</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ยังไม่ชำระ / โต๊ะที่ใช้งาน / ลูกค้า เป็นสถานะ ณ ตอนนี้ ไม่มีความหมายย้อนหลัง จึงซ่อนตอนดูเมื่อวานนี้ */}
        {!isPastDay && (<>
        <div className="card" style={{ background: '#1d1a12', border: '1px solid #4a3c1c' }}>
          <div style={{ color: 'var(--muted)', fontSize: '0.9rem', marginBottom: '0.5rem' }}>⏳ ยังไม่ชำระ</div>
          <div style={{ fontSize: 'clamp(1.4rem, 4vw, 1.8rem)', fontWeight: 700, color: 'var(--warn)', marginBottom: '0.75rem' }}>
            {formatNumber(revenueStats.unpaid)} ฿
          </div>
          <a onClick={() => navigate('/checkout')} style={{ color: 'var(--info)', fontSize: '0.85rem', cursor: 'pointer', textDecoration: 'none' }}>
            ดูโต๊ะที่ยังเปิดอยู่ →
          </a>
        </div>

        {/* โต๊ะที่ใช้งาน */}
        <div className="card" onClick={() => navigate('/tables')} style={{ cursor: 'pointer' }}>
          <div style={{ color: 'var(--muted)', fontSize: '0.9rem', marginBottom: '0.5rem' }}>🪑 โต๊ะที่ใช้งาน</div>
          <div style={{ fontSize: 'clamp(1.4rem, 4vw, 1.8rem)', fontWeight: 700, color: 'var(--text)' }}>
            {stats.summary.activeTables || 0}{allTables.length > 0 ? ` / ${allTables.length}` : ''} โต๊ะ
          </div>
          {openTables.length > 0 && (
            <div style={{ fontSize: '0.8rem', color: 'var(--muted)', marginTop: '0.5rem', overflowWrap: 'anywhere' }}>
              โต๊ะ {openTables.join(', ')}
            </div>
          )}
        </div>

        {/* ลูกค้า */}
        {stats.summary.totalGuests && stats.summary.totalGuests.total > 0 && (
          <div className="card" onClick={() => navigate('/tables')} style={{ cursor: 'pointer' }}>
            <div style={{ color: 'var(--muted)', fontSize: '0.9rem', marginBottom: '0.5rem' }}>👨‍👩‍👧‍👦 ลูกค้า</div>
            <div style={{ fontSize: 'clamp(1.4rem, 4vw, 1.8rem)', fontWeight: 700, color: 'var(--text)' }}>{stats.summary.totalGuests.total} คน</div>
            <div style={{ fontSize: '0.8rem', color: 'var(--muted)', marginTop: '0.5rem' }}>
              ผู้ใหญ่ {stats.summary.totalGuests.adults} · เด็ก {stats.summary.totalGuests.children130 + stats.summary.totalGuests.children120 + stats.summary.totalGuests.childrenUnder100}
            </div>
          </div>
        )}
        </>)}

        {/* ออเดอร์ (รวมจำนวนออเดอร์ + จำนวนรายการที่สั่ง เดิมเป็นการ์ดแยก) */}
        <div className="card" onClick={() => navigate('/orders')} style={{ cursor: 'pointer' }}>
          <div style={{ color: 'var(--muted)', fontSize: '0.9rem', marginBottom: '0.5rem' }}>📋 ออเดอร์</div>
          <div style={{ fontSize: 'clamp(1.4rem, 4vw, 1.8rem)', fontWeight: 700, color: 'var(--text)' }}>{stats.summary.totalOrders} ครั้ง</div>
          <div style={{ fontSize: '0.8rem', color: 'var(--muted)', marginTop: '0.5rem' }}>รวม {stats.summary.totalItems} รายการที่สั่ง</div>
        </div>
      </div>

      {/* แถวกราฟ 3:2 — แนวโน้มรายได้ / เมนูยอดนิยม */}
      <div className="dashboard-row-3-2" style={{ marginBottom: '1.5rem' }}>
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
            <h3 style={{ margin: 0, fontSize: '1.125rem', color: 'var(--text)' }}>📈 แนวโน้มรายได้</h3>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem', color: 'var(--text-2)', cursor: 'pointer', whiteSpace: 'nowrap' }}>
              <input
                type="checkbox"
                checked={showComparison}
                onChange={(e) => setShowComparison(e.target.checked)}
                style={{ cursor: 'pointer' }}
              />
              เทียบช่วงก่อนหน้า
            </label>
          </div>

          {/* Comparison Summary */}
          {showComparison && trendComparison && (
            <div style={{
              display: 'flex',
              gap: '1rem',
              marginBottom: '1rem',
              padding: '0.75rem',
              background: 'var(--surface-2)',
              borderRadius: '8px',
              fontSize: '0.875rem',
              flexWrap: 'wrap'
            }}>
              <div>
                <span style={{ color: 'var(--muted)' }}>ช่วงนี้: </span>
                <span style={{ fontWeight: 600, color: 'var(--info)' }}>{formatNumber(trendComparison.currentTotal)} ฿</span>
              </div>
              <div>
                <span style={{ color: 'var(--muted)' }}>ช่วงก่อนหน้า: </span>
                <span style={{ fontWeight: 600, color: 'var(--faint)' }}>{formatNumber(trendComparison.previousTotal)} ฿</span>
              </div>
              <div>
                <span style={{ color: 'var(--muted)' }}>เปลี่ยนแปลง: </span>
                <span style={{
                  fontWeight: 600,
                  color: trendComparison.percentChange === null ? 'var(--info)' : trendComparison.percentChange >= 0 ? 'var(--ok)' : '#ff9b9b'
                }}>
                  {trendComparison.percentChange === null
                    ? '🆕 ใหม่ (ช่วงก่อนหน้ายังไม่มีข้อมูล)'
                    : `${trendComparison.percentChange >= 0 ? '+' : ''}${trendComparison.percentChange}%`}
                </span>
              </div>
            </div>
          )}

          <Line
            data={trendChartData}
            options={{
              maintainAspectRatio: true,
              aspectRatio: 2,
              interaction: {
                mode: 'index',
                intersect: false,
              },
              plugins: {
                tooltip: {
                  enabled: true,
                  backgroundColor: 'rgba(0, 0, 0, 0.8)',
                  titleColor: '#fff',
                  bodyColor: '#fff',
                  padding: 12,
                  cornerRadius: 8,
                  displayColors: true,
                  callbacks: {
                    label: function(context) {
                      let label = context.dataset.label || '';
                      if (label) {
                        label += ': ';
                      }
                      if (context.parsed.y !== null) {
                        label += new Intl.NumberFormat('th-TH', {
                          style: 'currency',
                          currency: 'THB',
                          minimumFractionDigits: 2
                        }).format(context.parsed.y);
                      }
                      return label;
                    }
                  }
                }
              }
            }}
          />
        </div>

        <div className="card">
          <h3 style={{ margin: '0 0 1rem 0', fontSize: '1.125rem', color: 'var(--text)' }}>🏆 เมนูยอดนิยม</h3>
          <div style={{ color: 'var(--faint)', fontSize: '0.75rem', marginBottom: '0.75rem' }}>จำนวนที่สั่ง</div>
          {topItemsList.map((item, index) => (
            <div key={index} style={{ marginBottom: '0.6rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', marginBottom: '0.25rem', gap: '0.5rem' }}>
                <span style={{ color: 'var(--text-2)', minWidth: 0, overflowWrap: 'break-word' }}>{index + 1}. {item.name}</span>
                <span style={{ color: 'var(--text)', fontWeight: 600, flexShrink: 0 }}>{item.quantity}</span>
              </div>
              <div style={{ height: '8px', borderRadius: '999px', background: 'var(--surface-2)' }}>
                <div style={{ width: `${item.pct}%`, height: '100%', borderRadius: '999px', background: 'var(--accent)', opacity: item.opacity }} />
              </div>
            </div>
          ))}
          {(stats?.topSellingItems || []).length > 5 && (
            <a onClick={() => setTopItemsExpanded(v => !v)} style={{ color: 'var(--info)', fontSize: '0.85rem', cursor: 'pointer', textDecoration: 'none', display: 'inline-block', marginTop: '0.5rem' }}>
              {topItemsExpanded ? '← แสดงแค่ Top 5' : 'ดูทั้งหมด (Top 10) →'}
            </a>
          )}
        </div>
      </div>

      {/* ส่วนล่าง 5:7 — สั่งตามหมวด / ออเดอร์ล่าสุด */}
      <div className="dashboard-row-5-7" style={{ marginBottom: '1.5rem' }}>
        <div className="card">
          <h3 style={{ margin: '0 0 1rem 0', fontSize: '1.125rem', color: 'var(--text)' }}>📂 สั่งตามหมวด</h3>
          {categoryBars.map(cat => (
            <div key={cat.name} style={{ marginBottom: '0.6rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', marginBottom: '0.25rem', gap: '0.5rem' }}>
                <span style={{ color: 'var(--text-2)', minWidth: 0, overflowWrap: 'break-word' }}>{cat.name}</span>
                <span style={{ color: 'var(--text)', fontWeight: 600, flexShrink: 0 }}>{cat.pct}%</span>
              </div>
              <div style={{ height: '8px', borderRadius: '999px', background: 'var(--surface-2)' }}>
                <div style={{ width: `${cat.pct}%`, height: '100%', borderRadius: '999px', background: 'var(--info)' }} />
              </div>
            </div>
          ))}
          <div style={{ fontSize: '0.75rem', color: 'var(--faint)', marginTop: '0.75rem' }}>
            * ไม่นับหมวด "{EXCLUDED_CATEGORY_NAME}"
          </div>
        </div>

        <div className="card">
          <h3 style={{ margin: '0 0 1rem 0', fontSize: '1.125rem', color: 'var(--text)' }}>🕒 ออเดอร์ล่าสุด</h3>

          <div className="dashboard-recent-orders-table" style={{ overflowX: 'auto' }}>
            <table className="table-dark table-nowrap" style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--border)' }}>
                  <th style={{ padding: '0.75rem', textAlign: 'left', color: 'var(--muted)', fontWeight: 600 }}>เวลา</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', color: 'var(--muted)', fontWeight: 600 }}>โต๊ะ</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', color: 'var(--muted)', fontWeight: 600 }}>รายการ</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', color: 'var(--muted)', fontWeight: 600 }}>คิดเงินแยก</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', color: 'var(--muted)', fontWeight: 600 }}>สถานะ</th>
                </tr>
              </thead>
              <tbody>
                {stats.recentOrders.map((order, index) => {
                  const statusInfo = STATUS_LABELS[order.status];
                  const charged = order.separatelyChargedTotal ?? 0;
                  return (
                    <tr key={order.id} style={{ borderBottom: index < stats.recentOrders.length - 1 ? '1px solid var(--border)' : 'none' }}>
                      <td style={{ padding: '0.75rem', color: 'var(--text-2)' }}>{formatHM(order.createdAt)}</td>
                      <td style={{ padding: '0.75rem', color: 'var(--text)', fontWeight: 600 }}>{order.tableCode}</td>
                      <td style={{ padding: '0.75rem', color: 'var(--text-2)' }}>{order.itemCount} รายการ</td>
                      <td style={{ padding: '0.75rem' }}>
                        {charged > 0
                          ? <span style={{ color: 'var(--text)', fontWeight: 700 }}>{formatNumber(charged)} ฿</span>
                          : <span style={{ color: 'var(--faint)' }}>รวมในบุฟเฟ่ต์</span>}
                      </td>
                      <td style={{ padding: '0.75rem' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', color: statusInfo?.color || 'var(--faint)', fontWeight: 600, fontSize: '0.875rem' }}>
                          <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: statusInfo?.color || 'var(--faint)', flexShrink: 0 }} />
                          {statusInfo?.label || order.status}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* <768px: การ์ดรายแถวแทนตาราง (ตามสเปก responsive) */}
          <div className="dashboard-recent-orders-cards">
            {stats.recentOrders.map((order, index) => {
              const statusInfo = STATUS_LABELS[order.status];
              const charged = order.separatelyChargedTotal ?? 0;
              return (
                <div key={order.id} style={{ padding: '0.75rem 0', borderBottom: index < stats.recentOrders.length - 1 ? '1px solid var(--border)' : 'none' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.35rem' }}>
                    <span style={{ color: 'var(--text-2)', fontSize: '0.85rem' }}>{formatHM(order.createdAt)}</span>
                    <span style={{ color: 'var(--text)', fontWeight: 600 }}>{order.tableCode}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ color: 'var(--text-2)', fontSize: '0.85rem' }}>
                      {order.itemCount} รายการ ·{' '}
                      {charged > 0 ? <span style={{ color: 'var(--text)', fontWeight: 700 }}>{formatNumber(charged)} ฿</span> : <span style={{ color: 'var(--faint)' }}>รวมในบุฟเฟ่ต์</span>}
                    </span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', color: statusInfo?.color || 'var(--faint)', fontWeight: 600, fontSize: '0.8rem', flexShrink: 0 }}>
                      <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: statusInfo?.color || 'var(--faint)' }} />
                      {statusInfo?.label || order.status}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
